import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { BehaviorSubject, merge, Observable } from "rxjs";
import { map, scan, switchMap } from "rxjs/operators";
import { PaginatorDefaultOptions } from "src/app/common/components/paginator/paginator.component";
import { SLOT } from "src/app/common/enums/slot";
import { LoadingResponse } from "src/app/common/interfaces/loading-response";
import { getFileSize } from "src/app/common/utils/file-size";
import { ModuleConfigLogsItem } from "src/app/core/interfaces/project-entity";
import { TableAction } from "src/app/slots/three-year-plan/common/components/table/table.interfaces";
import {
  configurationAreaDownloadsColumnConfig,
  DOWNLOAD_TABS,
  ExtendedTabConfig
} from "./configuration-area-downloads.constants";
import { ConfigurationAreaDownloadsVM } from "./configuration-area-downloads.interfaces";
import { ConfigurationAreaDownloadsService, LogModules } from "./configuration-area-downloads.service";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "nb-configuration-area-downloads",
  templateUrl: "./configuration-area-downloads.component.html",
  styleUrls: ["./configuration-area-downloads.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfigurationAreaDownloadsService]
})
export class ConfigurationAreaDownloadsComponent implements OnInit {
  private readonly moduleChange = new BehaviorSubject<{ module: LogModules }>(undefined);
  private readonly hasMultiChange = new BehaviorSubject<{ hasMulti: boolean }>(undefined);

  private selectedLogs: Array<ModuleConfigLogsItem>;

  @Input() logType: "logs" | "reports";

  tabs: Array<ExtendedTabConfig> = [];
  readonly pagingConfig: PaginatorDefaultOptions = {
    pageSize: 20,
    pageSizeOptions: [20, 50, 200],
    showFirstLastButtons: true
  };
  readonly columnsConfig = configurationAreaDownloadsColumnConfig;
  readonly grafanaUrl$ = this.moduleChange.pipe(
    map(({ module }) => this.configurationAreaDownloadsService.getGrafanaUrl(module) ?? "")
  );

  vm$: Observable<ConfigurationAreaDownloadsVM>;
  currentTab$: Observable<ExtendedTabConfig>;

  constructor(private readonly configurationAreaDownloadsService: ConfigurationAreaDownloadsService) {
    this.vm$ = merge(this.moduleChange.asObservable(), this.hasMultiChange.asObservable(), this.getData()).pipe(
      scan((vm, values) => ({ ...vm, ...values }), {} as ConfigurationAreaDownloadsVM)
    );
  }

  public ngOnInit(): void {
    this.tabs = DOWNLOAD_TABS.filter(({ showFor }) => showFor.includes(this.logType));
    this.currentTab$ = this.moduleChange.pipe(map(({ module }) => this.tabs.find((item) => item.name === module)));

    this.moduleChange.next({ module: this.tabs[0].name });
    this.hasMultiChange.next({ hasMulti: false });
  }

  public onModuleChange(tabName: LogModules): void {
    this.moduleChange.next({ module: tabName });
  }

  public onTableAction(module: SLOT, action: TableAction<ModuleConfigLogsItem>): void {
    if (action.id === "download") {
      this.configurationAreaDownloadsService.downloadLogFile(module, action.row.fileName, this.logType);
    } else if (action.type === "select") {
      this.selectedLogs = action.selected;
      this.hasMultiChange.next({ hasMulti: action.selected.length > 1 });
    }
  }

  public downloadArchive(module: SLOT): void {
    const filesNames = this.selectedLogs.map(({ fileName }) => fileName);
    this.configurationAreaDownloadsService.downloadArchive(module, filesNames, this.logType);
  }

  private getData(): Observable<LoadingResponse<Array<ModuleConfigLogsItem>>> {
    return this.moduleChange.asObservable().pipe(
      switchMap(({ module }) => this.configurationAreaDownloadsService.getLogOrReportList(module, this.logType)),
      map(({ loading, result }) => ({
        loading,
        result: result.map((item) => ({ ...item, size: getFileSize(item.fileSizeInBites) }))
      }))
    );
  }
}
